import { useCallback } from 'react';
import useServiceAuth from './useServiceAuth';
import { isAuthError } from './ServiceAuthUtils';

/**
 * Custom hook for safely handling API mutations with automatic token refresh
 * and form data preservation in case of authentication failure.
 *
 * @param {Function} mutationFn - The mutation function to wrap (e.g., checksheetUpdate)
 * @param {string} formDataKey - Optional key to identify saved form data in session storage
 * @param {Object} reopenConfig - Optional configuration for modal reopen flag
 * @param {string} reopenConfig.flagKey - SessionStorage key for the reopen flag
 * @param {string} reopenConfig.valueField - Field in formDataToSave to use as flag value (defaults to 'true')
 * @returns {Object} - { executeMutation, savedFormData }
 *
 * @example
 * // Course list with dynamic reopen value
 * const { executeMutation } = useSafeMutation(
 *   checksheetUpdate,
 *   'courselist-form-data',
 *   { flagKey: 'courselist-modal-reopen', valueField: 'courseListId' }
 * );
 *
 * // Component with id-based reopen
 * const { executeMutation } = useSafeMutation(
 *   componentSave,
 *   'component-form-data',
 *   { flagKey: 'component-modal-reopen', valueField: 'id' }
 * );
 *
 * // Template with boolean reopen flag
 * const { executeMutation } = useSafeMutation(
 *   templateSave,
 *   'template-form-data',
 *   { flagKey: 'template-form-should-restore' }
 * );
 */
const useSafeMutation = (
  mutationFn,
  formDataKey = 'pat.saved.form.data',
  reopenConfig = null
) => {
  const { getAccessToken, saveFormDataToSession, getFormDataFromSession, redirectToServiceauth } =
    useServiceAuth();

  /**
   * Execute mutation with automatic token refresh and data preservation
   * @param {Object} params - Parameters for the mutation function (excluding token)
   * @param {Object} formDataToSave - Form data to save before mutation in case auth fails
   */
  const executeMutation = useCallback(
    async (params, formDataToSave = null) => {
      try {
        // Get fresh token (will auto-refresh if needed)
        const token = await getAccessToken();

        if (!token) {
          // Token refresh failed, save form data before redirect
          console.log('[useSafeMutation] No token available - saving data and redirecting');

          if (formDataToSave) {
            saveFormDataToSession(formDataToSave, formDataKey);

            // Set modal reopen flag if configured
            if (reopenConfig?.flagKey) {
              const reopenValue = reopenConfig.valueField
                ? formDataToSave[reopenConfig.valueField]
                : 'true';

              if (reopenValue) {
                sessionStorage.setItem(reopenConfig.flagKey, reopenValue);
              }
            }
          }

          // Trigger redirect to login
          await redirectToServiceauth();

          // This error won't be seen by user since redirect happens,
          // but helps with error handling in test environments
          throw new Error('Authentication failed - redirecting to login');
        }

        // Execute the mutation with fresh token
        const result = await mutationFn({ ...params, token });
        return result;
      } catch (error) {
        // Check for various auth/session failure indicators
        // This handles cases where getAccessToken returned a token but API call still failed with auth error
        if (formDataToSave && isAuthError(error)) {
          console.log('[useSafeMutation] Auth error during mutation - saving data and redirecting');

          saveFormDataToSession(formDataToSave, formDataKey);

          // Set modal reopen flag if configured
          if (reopenConfig?.flagKey) {
            const reopenValue = reopenConfig.valueField
              ? formDataToSave[reopenConfig.valueField]
              : 'true';

            if (reopenValue) {
              sessionStorage.setItem(reopenConfig.flagKey, reopenValue);
            }
          }

          // Trigger redirect to login
          await redirectToServiceauth();
        }
        throw error;
      }
    },
    [
      getAccessToken,
      mutationFn,
      saveFormDataToSession,
      formDataKey,
      reopenConfig,
      redirectToServiceauth,
    ]
  );

  /**
   * Retrieve any saved form data from previous session
   */
  const savedFormData = useCallback(() => {
    return getFormDataFromSession(formDataKey);
  }, [getFormDataFromSession, formDataKey]);

  return {
    executeMutation,
    savedFormData,
  };
};

export default useSafeMutation;
